import { concat, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { importSync } from '@embroider/macros';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { recordIdentifierFor } from '@warp-drive/core';
import can from 'ember-can/helpers/can';
import { t } from 'ember-intl';
import PowerSelect from 'ember-power-select/components/power-select';
import { and, notEq, or } from 'ember-truth-helpers';

import Button from 'umg-ember-backend/components/ui/button';
import Modal from 'umg-ember-backend/components/ui/modal';
import Window from 'umg-ember-backend/components/ui/window';
import storeOnRoute from 'umg-ember-backend/utils/decorators/store-on-route';
import { getBem } from 'umg-ember-backend/utils/get-bem';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

import Toolbar from '../index';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class RecordDetailVariantsComponent extends Component {
  // Services
  @service abilities;
  @service breadcrumb;
  @service flashMessages;
  @service history;
  @service intl;
  @service recordVariants;
  @service recordLocks;
  @service recordRelationships;
  @service recordStatus;

  // Defaults
  @storeOnRoute('routeStorageKey') selectedVariant = null;
  @storeOnRoute('routeStorageKey') scrollPositionVariant;
  @tracked showErrors = false;

  // Getter and setter
  get routeStorageKey() {
    return recordIdentifierFor(this.args.model).lid;
  }

  get isVariantsVisible() {
    return (
      this.abilities.can('viewVariants', this.args.model) &&
      this.recordVariants.hasVariants(this.args.model) &&
      !this.recordVariants.isVariant(this.args.model)
    );
  }

  get overline() {
    const variantOf = this.intl.t('record_detail.toolbar.variants.variant_of');

    return `${variantOf} ${this.args.model.displayLabel}`;
  }

  get title() {
    if (this.selectedVariant?.isNew) {
      return this.intl.t('record_detail.toolbar.variants.new');
    }

    return this.selectedVariant?.displayHeadline;
  }

  get RecordDetailForm() {
    let module = importSync(`../../forms/${getRecordType(this.args.model)}`);
    return module.default;
  }

  get variants() {
    return this.recordVariants.getVariants(this.args.model);
  }

  get isDisabled() {
    return this.recordStatus.isDisabled(this.args.model);
  }

  // Functions
  closeVariant = async () => {
    this.scrollPositionVariant = 0;

    if (this.recordLocks.isLockable(this.selectedVariant)) {
      await this.recordLocks.unlock(this.selectedVariant);
    }

    this.selectedVariant = null;
  };

  createVariant = () => {
    this.selectedVariant = this.recordVariants.createVariant(this.args.model);
  };

  deleteVariant = async (model) => {
    let ok = await this.history.confirmAction('delete', model);

    if (!ok) {
      return;
    }

    try {
      this.selectedVariant = null;

      await this.history.deleteModel(model);
      await this.recordVariants.reloadVariants(this.args.model);
    } catch (error) {
      await model.rollbackAttributes();
      await this.recordRelationships.rollback(model);

      this.flashMessages.serverError(error);
    }
  };

  forceUnlockVariant = (variant) => {
    return this.recordLocks.forceUnlock(variant);
  };

  saveVariant = async (variant) => {
    if (this.history.isModelInvalid(variant)) {
      this.showErrors = true;
      return;
    }

    await this.history.saveModel(variant);

    await this.recordVariants.reloadVariants(this.args.model);
  };

  // Template
  <template>
    {{#if this.isVariantsVisible}}
      <div class={{(bem)}} ...attributes>
        <div class={{bem 'select'}}>
          <PowerSelect
            @allowClear={{true}}
            @disabled={{or this.isDisabled @model.isNew}}
            @options={{this.variants}}
            @placeholder={{t 'record_detail.toolbar.variants.select'}}
            @selected={{@variantSelected}}
            @searchEnabled={{false}}
            @onChange={{fn (mut this.selectedVariant)}}
            as |option|
          >
            {{#if option.variantChannel.id}}
              {{option.variantChannel.displayLabel}}
            {{/if}}

            {{if (and option.cmsLanguageId option.variantChannel.id) ' - '}}

            {{#if option.cmsLanguageId}}
              {{t
                (concat
                  'forms.defaults.options.cmsLanguageId.' option.cmsLanguageId
                )
              }}
            {{/if}}

            {{#if (notEq option.cmsState 2)}}
              ({{t
                (concat
                  'forms.defaults.options.cmsState.' (or option.cmsState 2)
                )
              }})
            {{/if}}
          </PowerSelect>
        </div>
        <div class={{bem 'buttons'}}>
          {{#if (can 'create' @model)}}
            <Button
              @disabled={{or this.isDisabled @model.isNew}}
              @icon='add'
              @label={{t 'record_detail.toolbar.variants.new'}}
              {{on 'click' this.createVariant}}
            />
          {{/if}}
        </div>
      </div>

      <Modal
        @onClose={{this.closeVariant}}
        @dataDependencies={{this.selectedVariant}}
        as |variant onClose|
      >
        <Window
          @onBodyScroll={{fn (mut this.scrollPositionVariant)}}
          @scrollPosition={{this.scrollPositionVariant}}
        >
          <:overline>
            {{this.overline}}
          </:overline>

          <:title>
            {{this.title}}
          </:title>

          <:toolbar>
            <Toolbar
              @model={{variant}}
              @primaryActions={{hash
                onCancel=onClose
                onSave=(fn this.saveVariant variant)
              }}
              @secondaryActions={{hash
                onDeleteRecord=(fn this.deleteVariant variant)
                onForceUnlock=(fn this.forceUnlockVariant variant)
              }}
            />
          </:toolbar>

          <:body>
            <this.RecordDetailForm
              @model={{variant}}
              @recordType={{getRecordType variant}}
              @onSaveRecord={{fn this.saveVariant variant}}
              @showErrors={{this.showErrors}}
            />
          </:body>
        </Window>
      </Modal>
    {{/if}}
  </template>
}
